import { loadStripe } from "@stripe/stripe-js"
import { api } from "aldoo-ra/API"

let configPromise = null
let stripePromise = null
let publishableKeyOverride = null

/**
 * Lets a project set the publishable key itself (e.g. from deployment.js)
 * instead of the one the API reports in store_config. Call before any
 * payment UI renders.
 */
export function configureStripeClient({ publishableKey } = {}) {
    if (!publishableKey || publishableKey === publishableKeyOverride) return
    publishableKeyOverride = publishableKey
    stripePromise = null
}

/**
 * Fetches (and caches) the store config holding the publishable key.
 * Cheap — one API call, no Stripe.js download. Safe to call at boot.
 */
// on the server the answer is kept a minute (a page renders with the store's
// currency, which the admin may change in ParamIO); in the browser for the page
const SERVER_TTL_MS = 60 * 1000
let configAt = 0

export function prefetchStripeConfig() {
    const expired = typeof window === "undefined" && Date.now() - configAt > SERVER_TTL_MS
    if (configPromise && !expired) return configPromise
    configAt = Date.now()
    const storeAPI = api("store")
    configPromise = storeAPI({ store_config: true }).catch((err) => {
        console.error("[Stripe] store_config fetch failed:", err)
        configPromise = null
        return null
    })
    return configPromise
}

/**
 * Returns a singleton Stripe promise. Downloads Stripe.js from
 * js.stripe.com on FIRST call — call this only when a payment UI is
 * actually about to render, never at boot: the script injects its own
 * preconnects and ~13KiB on every page it loads on.
 */
export function getStripePromise() {
    if (stripePromise) return stripePromise
    if (publishableKeyOverride) {
        stripePromise = loadStripe(publishableKeyOverride)
        return stripePromise
    }
    stripePromise = prefetchStripeConfig().then((cfg) => {
        const key = cfg?.stripe_publishable_key
        if (!key) return null
        return loadStripe(key)
    })
    return stripePromise
}
